/**
 * Keyboard focus indicators for the design system.
 *
 * Before 2026-08-23 `focus-visible` appeared in **zero** files under
 * `src/Components/DesignSystem/`. `Button` (35 importing files), `Toggle`, `Tab`,
 * `Menu` items, `Dropdown` options and every icon-button had no focus indicator at
 * all, so a keyboard user could reach them and not be able to tell where they were.
 *
 * Three decisions are encoded here, each for a concrete reason:
 *
 * 1. **`outline`, not `ring`.** Tailwind's `ring-*` is a `box-shadow`. Both outlines
 *    and shadows are clipped by `overflow: hidden`/`auto` on an ancestor, but an
 *    outline can be pulled *inside* the element with a negative offset, which a
 *    box-shadow cannot. Hence the two variants below.
 *
 * 2. **`tw-outline` is not optional.** It supplies `outline-style: solid`. Width,
 *    offset and colour render nothing without it — a silent no-op of exactly the
 *    kind `tokenGuard.test.ts` cannot catch, because every class here is real.
 *
 * 3. **`focus-visible`, not `focus`**, so clicking a button with a mouse does not
 *    leave a ring behind. Text inputs are the deliberate exception: `TextField`,
 *    `Search` and `Dropdown`'s trigger keep their existing persistent `:focus`
 *    treatment, which is what a typist expects and which already passed 2.4.7.
 *
 * ROLE SPELLINGS since § 54: `accent` (blue-900 in light — 17.47:1 on white, well clear
 * of SC 1.4.11's 3:1) and `accent-hover` (blue-500 in light). Same rendered light
 * values; in dark they resolve to the brand cyan pair, and the ring OFFSET paints
 * `surface` rather than a hardcoded white — on a dark page a white halo around every
 * focused control would be the loudest thing on screen.
 */

/**
 * Standalone controls: buttons, toggles, tabs, icon buttons, form controls.
 *
 * **Two-tone**, via `ring` + `ring-offset`: the offset paints white between the
 * element and the ring, so the indicator reads on any ground — including a filled
 * blue button, where a bare blue outline nearly vanishes.
 *
 * **The outline is TRANSPARENT, not `none` (§ 224).** A ring is a box-shadow, and
 * Windows High Contrast (forced-colors) strips box-shadows — with `outline-none`
 * there was nothing left to recolour, so in that mode every ring-focused control
 * in the system had NO indicator at all. A transparent outline paints nothing in
 * normal themes (the ring underneath is the visible indicator, pixel-identical to
 * before) and forced-colors recolours it to the system Highlight — the standard
 * technique, and the reason `FOCUS_RING_INSET` below needed no change: it always
 * was a real outline.
 *
 * **No shadow (§ 369).** The ring is a box-shadow painted in front of `--tw-shadow`, so
 * it composes over whatever material the control already wears; a
 * `focus-visible:tw-shadow-*` here would REPLACE that material, not add to it. This
 * constant did exactly that until § 369. It added `--ds-glow`, § 253's dark-only halo.
 * § 261 then made `--ds-glow` the filled buttons' material (a top-light and a drop) in
 * both themes, and § 316 took the halo away. What was left marked focus by making a flat
 * control look like a primary button, and turned the red Delete button's drop navy. The
 * ring and its offset are the whole indicator: 3:1 on every ground in both themes
 * (`focusToken.test`), as in Carbon, Polaris, Atlassian and Primer.
 */
export const FOCUS_RING =
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-transparent focus-visible:tw-outline-offset-2 '
  /* § 366 — the ring's colour is the `focus` role, not `accent`: equal today, its own token so a
     retune of the brand accent is not silently a retune of every focus ring. */
  + 'focus-visible:tw-ring-2 focus-visible:tw-ring-focus '
  + 'focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-surface';

/**
 * § 395 — how far FOCUS_RING reaches OUTSIDE its element, in px: the 2px offset plus the 2px
 * ring (the transparent outline covers the same band). A scroll container clips anything past
 * its padding box, so a scroller whose items wear FOCUS_RING gives them this much padding —
 * `Tab`'s scrolling row does. px, not rem, like the ring itself: `tw-ring-2` does not follow the
 * user's font size, so the room must not either. `tabOverflow.test.tsx` derives this number
 * from the classes above, so a thicker ring cannot silently outgrow the room made for it.
 */
export const FOCUS_RING_EXTENT_PX = 4;

/**
 * Rows inside a clipping container — `Menu` (`overflow-hidden`), `Dropdown`'s
 * listbox (`overflow-y-auto`), `DropdownButton`'s menu, `Table`'s wrapper.
 *
 * **Deliberately still an outline, not the two-tone ring above.** A ring is a
 * `box-shadow`, and a box-shadow cannot be pulled inside the element — so in a
 * clipping container it is cut off at the edge. Measured in Chromium (§ 8): a +2px
 * ring on a menu row renders as an L-shape, missing its top and right edges. A
 * negative `outline-offset` draws the indicator inside the row instead, which is
 * the only variant that survives the clip.
 */
export const FOCUS_RING_INSET =
  'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:-tw-outline-offset-2 focus-visible:tw-outline-focus';

/**
 * Text fields: `TextField`, `Search`, `Dropdown`'s trigger.
 *
 * **A persistent `:focus`, not `focus-visible`** — a typist expects the field they
 * are typing in to stay marked, and clicking into a text field is not the case
 * `focus-visible` exists to suppress.
 *
 * **The ring is not decoration.** These fields also move their border to
 * `blue-500`, and a border-colour change ALONE is not enough: `blue-500` against
 * the resting `grey-550` border measures **1.77:1**, well under the 3:1 WCAG 1.4.11
 * asks of a state indicator. `Search` had exactly that — a colour swap and no ring,
 * with a bare `tw-outline-none` on the input removing the browser default that had
 * been doing the work. The ring against white is 5.62:1, which is what carries it.
 *
 * (Both figures moved on 2026-08-24 with the `blue-500` retune, from 1.54:1 and
 * 4.89:1. The conclusion is unchanged and the margin is wider — but the numbers in a
 * comment are only worth anything while they are the real ones.)
 *
 * All three components inlined these same four classes, which is how `Search` came
 * to be missing two of them without anyone noticing.
 *
 * The transparent outline is § 224, same as `FOCUS_RING`: the 1px ring is a
 * box-shadow that forced-colors strips, and this outline — invisible normally,
 * hugging the border at offset 0 — is what a focused field shows there instead.
 */
export const FOCUS_FIELD =
  'focus:tw-outline focus:tw-outline-2 focus:tw-outline-transparent '
  + 'focus:tw-border-accent-hover focus:tw-ring-1 focus:tw-ring-accent-hover';

/**
 * The same treatment for a field whose border lives on a WRAPPER rather than on the
 * focusable input — `Search`, where the icon, input and clear button share one
 * bordered row. `focus-within` puts the indicator on the element that draws the
 * boundary, so the whole field lights up rather than just the text area. The
 * transparent outline matters MOST here (§ 224): the inner input carries a bare
 * `tw-outline-none` (the wrapper is the indicator), so in forced-colors this
 * wrapper outline is the only focus signal a Search field has.
 */
export const FOCUS_FIELD_WITHIN =
  'focus-within:tw-outline focus-within:tw-outline-2 focus-within:tw-outline-transparent '
  + 'focus-within:tw-border-accent-hover focus-within:tw-ring-1 focus-within:tw-ring-accent-hover';
